import React, { useState } from 'react'
import { isNumber, isRequired, isValidRsaIdNumber, isValidEmail, isValidCellphoneNumber } from '../../../lib/validators'
import { WizardModal, CompleteBlob, SendDocumentModal, TermsAndConditionsLink } from '../../shared'
import { ActivityIndicator, Text } from '../../generic'
import gql from 'graphql-tag'
import { sittingOnMoney } from '../../../images'
import { useApolloClient, useQuery } from '@apollo/react-hooks'
import firebase from 'gatsby-plugin-firebase'
import MokuHoldingCard from '../../shared/MokuHoldingCard'
import QuickQuotePrice from '../../funeral/QuickQuotePrice/QuickQuotePrice'
import { printCurrency } from '../../../lib/utils'
import BankDetails from '../../savings/BankDetails'

const MY_DETAILS = gql`{
  myClient {
    id
    idNumber
    firstNames
    surname
    cellNumbers
    email
  }
}`

const GET_ASSESSMENT_DETAILS = gql`
  query($assessmentId: String!) {
    creditWorthinessAssessment(id: $assessmentId) {
      id
      paymentAmount
      reportNumber
    }
  }
`


const GET_CREDIT_REPORT_CHALLENGE = gql`query($idNumber:String!, $income: Float) {
  getChallenge(idNumber: $idNumber, income: $income) {
    error
    telephones
    employers
    accounts
  }
}`

const GET_CREDIT_REPORT_ACCESS = gql`query($idNumber: String!, $telephone: String, $employer: String, $account: String) {
  doChallenge(idNumber: $idNumber, telephone: $telephone, employer: $employer, account: $account)   {
    passedChallenge
    accessCode
  }
}`

const CREATE_CREDIT_WORTHINESS = gql`
  mutation($creditWorthiness: CreateCreditWorthinessInput, $client: CreateClientInput, $accessCode: String) {
    createSalesInteractionCreditWorthiness(creditWorthiness: $creditWorthiness, client: $client, accessCode: $accessCode, salesInteraction: {}) {
      id
      paymentAmount
      reportNumber
      accessCode
    } 
  }
`

const SIGN_CREDIT_WORTHINESS = gql`
  mutation($creditWorthinessId: String, $signature: String!, $accessCode: String) {
    signCreditWorthiness(creditWorthinessId: $creditWorthinessId, signature: $signature, accessCode: $accessCode) {
      accessCode
    }
  }
`

const OZOW_PAYMENT = gql`
query ($redirectUrl: String!, $associatedProducts: [ProductAmountInput!]!) {
    generateOzowPaymentUrl(redirectUrl: $redirectUrl, associatedProducts: $associatedProducts)
  }
`

const buildSteps = (setOpen, setIdNumber, setQuestionData, setAccessCode, source, creditWorthinessId, skipIdentityValidation, startAtPayment, skipUserDetails) => {
  let accessCode

  return [{
    name: "initialRoute",
    nextAfterDelay: 1,
    nextSteps: creditWorthinessId ? "complete" : (startAtPayment ? "howPay" : (skipUserDetails ? "createAssessment" : "step1"))
  }, {
    name: "step1",
    beforeStep: () => {
      firebase.analytics().logEvent("CWA_START");
    },
    title: "Credit Worthiness Assessment",
    title2: "Please enter your details so that we can perform your credit worthiness assessment",
    fieldNames: ["idNumber", "firstNames", "surname", "cellNumber", "whatsAppNumber", "email", "acceptTerms"],
    nextTitle: "Next",
    nextSteps: skipIdentityValidation ? ["createAssessment"] : ["verificationStep1"]
  },
  {
    name: "createAssessment",
    beforeStep: async ({ apolloClient, chatState, changeFieldValue }) => {
      firebase.analytics().logEvent("CWA_LOGGED_IN_CREATE");

      const { idNumber, cellNumber, email, acceptTerms, idDocumentId, whatsAppNumber } = chatState
      setIdNumber(idNumber)

      const result = await apolloClient.mutate({ mutation: CREATE_CREDIT_WORTHINESS, variables: { client: {
        idNumber,
        cellNumbers: [cellNumber],
        email,
        acceptedTerms: acceptTerms,
        whatsAppNumber
      },
      idDocumentId,
      accessCode,
      creditWorthiness: {
        ...(source ? { referredFrom: source } : {}),
        identityVerified: true
      } } })

      console.log(result.data.createSalesInteractionCreditWorthiness)

      changeFieldValue('creditWorthinessId', result.data.createSalesInteractionCreditWorthiness.id)
      changeFieldValue('paymentAmount', result.data.createSalesInteractionCreditWorthiness.paymentAmount)
      changeFieldValue('reportNumber', result.data.createSalesInteractionCreditWorthiness.reportNumber)
    },
    component: ActivityIndicator,
    nextAfterDelay: 1000,
    noNext: true,
    nextSteps: ["infoStep"]
  },
  {
    name: "verificationStep1",
    beforeStep: async ({ apolloClient, chatState, changeFieldValue }) => {
      firebase.analytics().logEvent("CWA_VERIFICATION_START");
      
      const { idNumber, cellNumber, email, acceptTerms, idDocumentId, whatsAppNumber } = chatState
      setIdNumber(idNumber)

      const result = await apolloClient.mutate({ mutation: CREATE_CREDIT_WORTHINESS, variables: { client: {
        idNumber,
        cellNumbers: [cellNumber],
        email,
        acceptedTerms: acceptTerms,
        whatsAppNumber
      },
      idDocumentId,
      accessCode, creditWorthiness: source ? {
        referredFrom: source } : null } })

      console.log(result.data.createSalesInteractionCreditWorthiness)
      
      changeFieldValue('creditWorthinessId', result.data.createSalesInteractionCreditWorthiness.id)
      changeFieldValue('paymentAmount', result.data.createSalesInteractionCreditWorthiness.paymentAmount)
      changeFieldValue('reportNumber', result.data.createSalesInteractionCreditWorthiness.reportNumber)

      const result2 = await apolloClient.query({ query: GET_CREDIT_REPORT_CHALLENGE, variables: { idNumber } })
      setQuestionData(result2.data.getChallenge)
    },
    title: "Credit Worthiness Assessment - Challenge Questions",
    title2: "Please answer the following questions to validate your identity",
    fieldNames: ["telephone"],
    nextSteps: ["verificationStep2"]
  }, {
    name: "verificationStep2",
    title: "Credit Worthiness Assessment - Challenge Questions",
    title2: "Please answer the following questions to validate your identity",
    fieldNames: ["employer"],
    nextSteps: ["verificationStep3"]
  },
  {
    name: "verificationStep3",
    title: "Credit Worthiness Assessment - Challenge Questions",
    title2: "Please answer the following questions to validate your identity",
    fieldNames: ["account"],
    nextSteps: ["performVerification"]
  },
  {
    name: "performVerification",
    beforeStep: async ({ apolloClient, chatState }) => {
      const { idNumber, telephone, employer, account } = chatState
      const result = await apolloClient.query({ query: GET_CREDIT_REPORT_ACCESS, variables: { idNumber, telephone, employer, account } })

      console.log(result)

      if (result.data.doChallenge && result.data.doChallenge.passedChallenge) {
        accessCode = result.data.doChallenge.accessCode
      } else {
        accessCode = null
      }

      if (accessCode) {
        firebase.analytics().logEvent("CWA_VERIFICATION_SUCCESS");
      } else {
        firebase.analytics().logEvent("CWA_VERIFICATION_ERROR");
      }
      setAccessCode(accessCode)
    },
    component: ActivityIndicator,
    nextAfterDelay: 1000,
    noNext: true,
    nextSteps: () => accessCode ? ["infoStep"] : ["error"]
  },
  {
    name: "infoStep",
    beforeStep: ({ chatState }) => {
      firebase.analytics().logEvent("CWA_INFO_STEP");
    },
    text: ({ paymentAmount }) => <><MokuHoldingCard>
      <QuickQuotePrice title="Meerkat Credit Worthiness Assessment" price={`${printCurrency(paymentAmount)}`} color="white" />
    </MokuHoldingCard><p />
    <center>
      <p /><p />
    <Text size={24} bold>What&apos;s in it for you...</Text>
<div style={{ textAlign: "left", padding: "8px" }}>
      <div style={{ marginBottom: "16px", padding: "16px", background: "#FFFFFF", boxShadow: "inset 0px 0px 10px rgba(64, 67, 87, 0.08)", borderRadius: "8px", display: "flex", alignItems: "center", gap: "12px" }}>
        <div style={{ fontSize: "32px", minWidth: "40px" }}>💚</div>
        <div>
          <Text size={14} bold>Check your credit health</Text>
          <div style={{ height: "4px" }} />
          <Text size={10}>See your debt, spending, and affordability.</Text>
        </div>
      </div>
      <div style={{ marginBottom: "16px", padding: "16px", background: "#FFFFFF", boxShadow: "inset 0px 0px 10px rgba(64, 67, 87, 0.08)", borderRadius: "8px", display: "flex", alignItems: "center", gap: "12px" }}>
        <div style={{ fontSize: "32px", minWidth: "40px" }}>💡</div>
        <div>
          <Text size={14} bold>Get simple tips</Text>
          <div style={{ height: "4px" }} />
          <Text size={10}>Improve your money and credit standing.</Text>
        </div>
      </div>
      <div style={{ marginBottom: "16px", padding: "16px", background: "#FFFFFF", boxShadow: "inset 0px 0px 10px rgba(64, 67, 87, 0.08)", borderRadius: "8px", display: "flex", alignItems: "center", gap: "12px" }}>
        <div style={{ fontSize: "32px", minWidth: "40px" }}>🎯</div>
        <div>
          <Text size={14} bold>Know your chances</Text>
          <div style={{ height: "4px" }} />
          <Text size={10}>See if you&apos;re likely to qualify for loans.</Text>
        </div>
      </div>
    </div>
    </center>
    </>,
    nextButtons: () => {
      return [
        { label: "Continue", nextSteps: ["howPay"], buttonProps: { square: false } },
        {
          label: "No Thanks",
          nextSteps: ["done"],
          buttonProps: {
            square: false,
            inverted: true,
            style: { border: 'none' }
          },
          onClick: () => {
            firebase.analytics().logEvent("CWA_NO_THANKS");
          }
        }
      ]
    },
    nextButtonsInColumn: true
  },
  {
    name: "getSignature",
    beforeStep: async () => {
      firebase.analytics().logEvent("CWA_GET_SIGNATURE");
    },
    title: "Clear My Name",
    title2: "Add your signature to make it official",
    title3: <>
      You will be signing the following documents that we use to clear your name. We will fill them in for you.
      <p />
      <ul style={{ textAlign: "left", fontSize: "12px", margin: 0 }}>
        <li>Application Form</li>
        <li>Consent to Transfer of Debt Counsellor so we can issue a certificate</li>
        <li>Power of Attorney to act on your behalf</li>
      </ul>
    </>,
    fieldNames: ["signature"],
    nextSteps: ["doSign"]
  },
  {
    beforeStep: async ({ apolloClient, chatState }) => {
      const { signature, creditWorthinessId } = chatState
      const result = await apolloClient.mutate({ mutation: SIGN_CREDIT_WORTHINESS, variables: { signature, creditWorthinessId, accessCode } })
      console.log(result)
      accessCode = result.data.signCreditWorthiness.accessCode
      setAccessCode(accessCode)
    },
    name: "doSign",
    noNext: true,
    nextAfterDelay: 1000,
    nextSteps: ["howPay"]
  },
  {
    name: "howPay",
    text: ({ paymentAmount }) => <><MokuHoldingCard>
      <QuickQuotePrice title="Meerkat Credit Worthiness Assessment" price={`${printCurrency(paymentAmount)}`} color="white" />
    </MokuHoldingCard><p /></>,
    nextButtons: () => {
      let buttons = []
        buttons.push(
          { label: "Instant EFT", nextSteps: ["instantEFTLink"], buttonProps: { square: false } }, 
          { label: "Manual EFT", nextSteps: ["manualEFT"], buttonProps: { square: false } }, 
        )
      /*buttons.push(
        /*{ label: "Once Off Debit Order", nextSteps: ["done"], buttonProps: { square: true } },
        { label: "Easy Pay", nextSteps: ["done"], buttonProps: { square: true } },*//*
        { label: "DebiCheck", nextSteps: ["ownAccount"], buttonProps: { square: false } },
      )*/
      return buttons
    },
    nextButtonsInColumn: true,
    showBack: true,
  }, 
  {
    name: "instantEFTLink",
    title: "Redirect to payment partner",
    title2: (
      <>
        <p />
        <Text size={16}>
          After clicking the below button, we will redirect you to our secure
          payment partner to complete the transaction
        </Text>
        <p />
      </>
    ),
    handleNext: async ({ chatState, apolloClient }) => {
      firebase.analytics().logEvent("CWA_INSTANT_EFT_PAYMENT", {})
      const { creditWorthinessId } = chatState
      const { data } = await apolloClient.mutate({
        mutation: OZOW_PAYMENT, variables:
        {
          redirectUrl: skipUserDetails ? window.location.href : `${window.location.origin}/credit-worthiness?id=${creditWorthinessId}`,
          associatedProducts: [{
            productId: creditWorthinessId,
            productType: 'CREDIT_WORTHINESS',
            amount: chatState.paymentAmount,
          }]
        }
      })
      const paymentUrl = data.generateOzowPaymentUrl
      window.location.href = paymentUrl;
    }
  },
  {    
    beforeStep: () => {
      firebase.analytics().logEvent("CWA_MANUAL_EFT_PAYMENT", {})
    },
    name: "manualEFT",
    title: ({ paymentAmount, reportNumber }) => <><MokuHoldingCard>
      <QuickQuotePrice title="Credit Worthiness Assessment" disclaimer="" price={printCurrency(paymentAmount)} color="white" />
    </MokuHoldingCard><p /></>,
    title2: "Please make an EFT payment using the following details",
    component: ({ chatState }) => <BankDetails reference={chatState.reportNumber} />,
    nextTitle: "Done",
    nextSteps: ["complete"],
    showBack: true,
  },
  {
    name: "complete",
    title: "Credit Worthiness Assessment",
    title2: <CompleteBlob image={sittingOnMoney} title="You're all set!" description="Your Credit Worthy Assessment will be emailed to you once we have received payment" />,
    nextButtonsInColumn: true,
    nextButtons: [{ label: "Done", nextSteps: ["done"] } /*, { label: "Email My report", onClick: () => { firebase.analytics().logEvent("CREDIT_REPORT_REQUEST_EMAIL_REPORT"); setEmailModal(true) }, noNextStep: true }*/]
  },
  {
    name: "error",
    title: "Credit Worthiness Assessment",
    title2: <CompleteBlob image={sittingOnMoney} title="Oops!" description="Something went wrong." />,
    nextButtonsInColumn: true,
    nextButtons: [{ label: "Let's try again", nextSteps: ["step1"] }, { label: "Nevermind, I'm out", onClick: () => setOpen(false), noNextStep: true }]
  },
  {
    name: "done",
    beforeStep: () => {
      setOpen(false)
    } 
  }] 
} 

const fields = {
  idNumber: {
    type: "text",
    label: "Your ID Number",
    placeholder: "Enter 13 digit ID number",
    validate: [isValidRsaIdNumber, isRequired],
    required: true
  },
  firstNames: {
    label: "First Name",
    validate: [isRequired],
    required: true
  },
  surname: {
    label: "Surname",
    validate: [isRequired],
    required: true
  },
  email: {
    label: "Email",
    validate: [isRequired, isValidEmail],
    required: true
  },
  cellNumber: {
    label: "Cell Number",
    validate: [isRequired, isValidCellphoneNumber],
    required: true
  },
  telephone: {
    label: "What is your telephone number?",
    type: "radiogroup",
    options: [],
    validate: [isRequired]
  },
  income: {
    label: "What is your net-income?",
    type: "currency",
    validate: [isRequired, isNumber],
  },
  employer: {
    label: "Who is your employer?",
    type: "radiogroup",
    options: [],
    validate: [isRequired]
  },
  account: {
    label: "Who do you have an account with?",
    type: "radiogroup",
    options: [],
    validate: [isRequired]
  },
  acceptTerms: {
    type: "checkbox",
    label: <>Accept our <TermsAndConditionsLink>Terms of Services and Privacy Policy</TermsAndConditionsLink></>,
    validate: value => value === true ? undefined : "You need to accept terms and conditions to continue"
  },
  signature: {
    type: "signature",
    validate: [isRequired]
  },
  whatsAppNumber:{
    type: "whatsappOpt",
    cellNumberField: "cellNumber" ,
    validate: [isValidCellphoneNumber]
  }
}

function GetCreditWorthiness({ open, setOpen, idNumber, setIdNumber, skipIdentityValidation = false, startAtPayment = false, skipUserDetails = false, initialChatState = {}, assessmentId }) {
  const [accessCode, setAccessCode] = useState(null)
  const apolloClient = useApolloClient()
  const { data, loading } = useQuery(MY_DETAILS)
  const { data: assessmentData, loading: assessmentLoading } = useQuery(GET_ASSESSMENT_DETAILS, {
    variables: { assessmentId },
    skip: !assessmentId
  })

  let { myClient } = data || {}
  if (!myClient) {
    myClient = {}
  }

  const assessment = assessmentData?.creditWorthinessAssessment || {}

  if (loading || assessmentLoading) { return <div /> }

  const source = window && (new URLSearchParams(window.location.search).get("utm_source"))
  const creditWorthinessId = window && (new URLSearchParams(window.location.search).get("id"))

  const setQuestionData = ({ telephones, accounts, employers }) => {
    fields.telephone.options = telephones
    fields.account.options = accounts
    fields.employer.options = employers
  }

  return <>
    <WizardModal
      name="creditWorthiness"
      open={open}
      setOpen={setOpen}
      steps={buildSteps(setOpen, setIdNumber, setQuestionData, setAccessCode, source, creditWorthinessId, skipIdentityValidation, startAtPayment, skipUserDetails)}
      initialValues={{
        idNumber: idNumber || myClient.idNumber,
        firstNames: myClient.firstNames,
        surname: myClient.surname,
        cellNumber: myClient.cellNumbers && myClient.cellNumbers[0],
        whatsAppNumber: myClient.cellNumbers && myClient.cellNumbers[0],
        email: myClient.email,
        acceptTerms: true,
        ...(assessmentId ? {
          creditWorthinessId: assessment.id,
          paymentAmount: assessment.paymentAmount,
          reportNumber: assessment.reportNumber
        } : {}),
        ...initialChatState
      }}
      fields={fields}
    />
  </>
}

export default GetCreditWorthiness
